import { useState, type FormEvent } from 'react';
import { isPlausibleEmail, type KioskSubmission } from '@ecc/shared';
import { AcsButton, AcsCheckbox, AcsInput } from '../components';
import { useI18n } from '../i18n/I18nProvider';
import type { KioskConfig } from './api';
import { PolicyDialog } from './PolicyDialog';
import './kiosk.css';

/**
 * Modulo di raccolta (§7.2, §7.3).
 *
 * Struttura non negoziabile:
 *  - la casella marketing non è mai preselezionata e non è mai obbligatoria;
 *  - il ricontatto commerciale è una casella distinta, che non implica marketing;
 *  - il link all'informativa è visibile prima dell'invio;
 *  - il pulsante Invia non sottintende alcun consenso;
 *  - non esiste una casella unica per finalità diverse, né la formula
 *    "accetto la privacy": l'informativa si riceve, non si accetta.
 */

export type KioskFormValues = Omit<KioskSubmission, 'submissionId' | 'submittedAt'>;

type Errors = Partial<Record<'firstName' | 'lastName' | 'email' | 'items', string>>;

export function KioskForm({
  config,
  submitting,
  preview = false,
  onSubmit,
}: {
  config: KioskConfig;
  submitting: boolean;
  /**
   * Anteprima per il personale: il modulo si vede ma non si invia.
   * Il blocco è strutturale — `handleSubmit` esce prima di chiamare `onSubmit`
   * e il pulsante è disabilitato — così l'anteprima non può registrare nulla.
   */
  preview?: boolean;
  onSubmit: (values: KioskFormValues) => void;
}) {
  const { t } = useI18n();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [items, setItems] = useState<string[]>([]);
  const [callbackRequested, setCallbackRequested] = useState(false);
  /**
   * Stato iniziale `false`, sempre.
   * Non viene mai inizializzato da record precedenti né da preferenze: §23
   * vieta di preselezionare la casella sulla base di consensi passati.
   */
  const [marketingGranted, setMarketingGranted] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  function toggleItem(item: string): void {
    setItems((current) =>
      current.includes(item) ? current.filter((i) => i !== item) : [...current, item],
    );
  }

  function validate(): Errors {
    const next: Errors = {};
    if (firstName.trim().length === 0) next.firstName = t.kiosk.errorRequired;
    if (lastName.trim().length === 0) next.lastName = t.kiosk.errorRequired;
    if (!isPlausibleEmail(email)) next.email = t.kiosk.errorEmail;
    if (items.length === 0) next.items = t.kiosk.errorNoSelection;
    return next;
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    if (preview) return;
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    onSubmit({
      eventId: config.event.id,
      contact: {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        ...(company.trim() ? { company: company.trim() } : {}),
        email: email.trim(),
      },
      // `resourceId` documenta *quale* materiale è stato chiesto; `type` resta
      // la finalità sotto cui viene trattato.
      requestedItems: items.map((id) => {
        const item = config.requestableItems.find((candidate) => candidate.id === id);
        return { type: item?.type ?? 'DOCUMENT', resourceId: id };
      }),
      callbackRequested,
      // Il consenso è dichiarato esplicitamente in entrambi i casi, e quando è
      // prestato porta con sé la versione del testo effettivamente mostrato.
      marketingConsent:
        marketingGranted && config.marketingConsentText
          ? {
              granted: true,
              consentTextId: config.marketingConsentText.consentTextId,
              consentTextVersion: config.marketingConsentText.version,
              consentTextLanguage: config.marketingConsentText.language,
            }
          : { granted: false },
      policy: {
        policyId: config.policy.policyId,
        policyVersion: config.policy.version,
        policyLanguage: config.policy.language,
      },
      language: config.language,
    });
  }

  return (
    <form className="kiosk-form" onSubmit={handleSubmit} noValidate>
      <h1 className="kiosk-form__title">{t.kiosk.title}</h1>

      {/*
        `autoComplete="off"` sull'intero form: il tablet è condiviso fra
        visitatori e i suggerimenti del browser mostrerebbero i dati di chi ha
        compilato prima (§7.3).
      */}
      <AcsInput
        label={t.kiosk.firstName}
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        autoComplete="off"
        autoCapitalize="words"
        error={errors.firstName}
        required
      />
      <AcsInput
        label={t.kiosk.lastName}
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        autoComplete="off"
        autoCapitalize="words"
        error={errors.lastName}
        required
      />
      <AcsInput
        label={t.kiosk.company}
        optionalHint={t.kiosk.optional}
        value={company}
        onChange={(e) => setCompany(e.target.value)}
        autoComplete="off"
      />
      <AcsInput
        label={t.kiosk.email}
        type="email"
        inputMode="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        error={errors.email}
        required
      />

      <fieldset className="kiosk-form__group">
        <legend className="acs-field__label">{t.kiosk.interests}</legend>
        {/*
          Le voci arrivano dalla configurazione della fiera, già tradotte nella
          lingua servita. Nessuna è spuntata all'avvio: `items` parte vuoto e la
          configurazione non ha modo di dire il contrario (§23).
        */}
        {config.requestableItems.map((item) => (
          <AcsCheckbox
            key={item.id}
            label={item.label}
            checked={items.includes(item.id)}
            onChange={() => toggleItem(item.id)}
          />
        ))}
        {errors.items ? (
          <span className="acs-field__error" role="alert">
            {errors.items}
          </span>
        ) : null}
      </fieldset>

      {/*
        Richiesta di ricontatto: è una richiesta dell'interessato, tenuta
        separata dal marketing e priva di qualunque enfasi che la accomuni a
        esso (§2.2).
      */}
      <div className="kiosk-form__group">
        <AcsCheckbox
          // Etichetta della fiera se configurata, altrimenti quella standard.
          // Comunque sia scritta, la spunta produce una richiesta
          // COMMERCIAL_CALLBACK: la finalità non è personalizzabile (§2.2).
          label={config.callbackLabel ?? t.kiosk.callback}
          checked={callbackRequested}
          onChange={(e) => setCallbackRequested(e.target.checked)}
        />
      </div>

      <hr className="kiosk-form__separator" />

      {/*
        Sezione marketing: separata visivamente e testualmente, facoltativa, e
        senza alcun colore o peso che la renda più invitante delle altre (§20.8).
        Se il testo di consenso non è pubblicato nella lingua corrente, la
        casella non compare affatto: meglio non raccogliere che raccogliere un
        consenso non informato (§21).
      */}
      {config.marketingConsentText ? (
        <fieldset className="kiosk-form__group">
          <legend className="acs-field__label">{t.kiosk.marketingSectionTitle}</legend>
          <AcsCheckbox
            label={config.marketingConsentText.text}
            checked={marketingGranted}
            onChange={(e) => setMarketingGranted(e.target.checked)}
          />
        </fieldset>
      ) : null}

      <div className="kiosk-form__actions">
        <AcsButton variant="secondary" onClick={() => setPolicyOpen(true)}>
          {t.kiosk.readPolicy}
        </AcsButton>
        <AcsButton type="submit" disabled={submitting || preview}>
          {submitting ? t.kiosk.submitting : t.kiosk.submit}
        </AcsButton>
      </div>

      {policyOpen ? (
        <PolicyDialog policy={config.policy} onClose={() => setPolicyOpen(false)} />
      ) : null}
    </form>
  );
}
